import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, KeyRound, Pencil, Loader2, Power, CheckCircle2, Globe, TrendingUp } from 'lucide-react';
import {
  igTradingService as svc,
  type IgSettings,
  type IgAccount,
} from '@/services/igTradingService';

/**
 * Configuration → IG Markets.
 * Manages the user's IG credentials (demo + live, secrets masked) and proves the
 * connection. IG trades US shares + forex as CFDs. Demo and live are separate
 * logins with separate API keys — pick the environment with the toggle.
 */
export const IgTradingSettingsPage: React.FC = () => {
  const [demo, setDemo] = useState(true);
  const [settings, setSettings] = useState<IgSettings | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [account, setAccount] = useState<IgAccount | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const refresh = useCallback(async (isDemo: boolean) => {
    setLoaded(false);
    try {
      setSettings(await svc.getSettings(isDemo));
    } finally {
      setLoaded(true);
    }
    // Account is best-effort — a 404 just means "no creds / not connected yet".
    try {
      setAccount(await svc.getAccount(isDemo));
      setAccountError(null);
    } catch (e: any) {
      setAccount(null);
      setAccountError(e?.message || 'not connected');
    }
  }, []);

  useEffect(() => { void refresh(demo); }, [refresh, demo]);

  const enabled = !!settings?.liveTradingEnabled;
  const hasCreds = !!settings && settings.apiKeyMasked !== '**** unset';

  const applyToggle = async (next: boolean) => {
    setToggling(true);
    setToggleError(null);
    try {
      await svc.setEnabled(next);
      setSettings((s) => (s ? { ...s, liveTradingEnabled: next } : s));
      void refresh(demo);
    } catch (e: any) {
      setToggleError(e?.message || 'Failed to update kill switch');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">IG Markets</h2>
        <p className="text-xs text-muted-foreground">
          Connect your IG account to trade US shares and forex as CFDs.
        </p>
      </div>

      {/* Environment toggle */}
      <div className="inline-flex rounded-md border p-0.5 text-xs">
        {[{ k: true, label: 'Demo' }, { k: false, label: 'Live' }].map(({ k, label }) => (
          <button
            key={label}
            onClick={() => setDemo(k)}
            className={`px-3 py-1 rounded-[5px] font-medium transition-colors ${
              demo === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* CFD / environment warning */}
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
        <span className="text-muted-foreground">
          IG instruments are <span className="font-semibold">CFDs</span> (leveraged) — not real shares.{' '}
          {demo
            ? 'Demo orders use no real money.'
            : <>Live orders use <span className="font-semibold">real money</span>. The kill switch is OFF by default.</>}
        </span>
      </div>

      {/* Live kill switch — only meaningful for the live account */}
      {!demo && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Power className="h-4 w-4 text-primary" />
              Live Trading (my account)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="rounded-md border bg-card/50 p-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Real-Money IG Trading</span>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    enabled ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {enabled ? 'ARMED' : 'OFF'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {!hasCreds
                    ? 'Add your live IG credentials below first.'
                    : enabled ? 'Live IG orders can fire (subject to the master switch).' : 'Live IG orders are suppressed.'}
                </div>
                {toggleError && (
                  <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {toggleError}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant={enabled ? 'outline' : 'default'}
                disabled={!hasCreds || toggling}
                onClick={() => void applyToggle(!enabled)}
              >
                {toggling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {enabled ? 'Disarm' : 'Arm live trading'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connection status */}
      {account ? (
        <div className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Connected — account {account.accountNumber} · equity {account.equity ?? '—'} {account.status ? `· ${account.status}` : ''}
        </div>
      ) : hasCreds && accountError ? (
        <div className="text-[11px] text-muted-foreground">Connection: {accountError}</div>
      ) : null}

      {hasCreds && <IgOrderCard demo={demo} />}

      <IgCredentialsCard demo={demo} settings={settings} loaded={loaded} onSaved={() => refresh(demo)} />
    </div>
  );
};

const IgOrderCard: React.FC<{ demo: boolean }> = ({ demo }) => {
  const [symbol, setSymbol] = useState('');
  const [assetClass, setAssetClass] = useState<'equity' | 'forex'>('equity');
  const [quantity, setQuantity] = useState('1');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (side: 'buy' | 'sell') => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const qty = Number(quantity);
      if (!symbol.trim() || !(qty > 0)) { setErr('Enter a symbol and a positive size.'); return; }
      const r = await svc.placeOrder(demo, { symbol: symbol.trim(), side, quantity: qty, assetClass });
      setMsg(`${side.toUpperCase()} ${qty} ${symbol.trim()} — deal ${r.orderId}`);
    } catch (e: any) {
      setErr(e?.message || 'Order failed');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'h-8 rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          Manual order ({demo ? 'demo' : 'live'})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {!demo && (
          <div className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Real money — needs the master switch + your IG live kill switch ON.
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="symbol e.g. EURUSD" className={`${inputCls} w-36 font-mono`} />
          <select value={assetClass} onChange={(e) => setAssetClass(e.target.value as 'equity' | 'forex')} className={inputCls}>
            <option value="equity">Equity</option>
            <option value="forex">Forex</option>
          </select>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" min="0" step="any" className={`${inputCls} w-20`} />
          <Button size="sm" disabled={busy} onClick={() => void submit('buy')}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buy'}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void submit('sell')}>Sell</Button>
        </div>
        {msg && <div className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {msg}</div>}
        {err && <div className="text-[11px] text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {err}</div>}
      </CardContent>
    </Card>
  );
};

const IgCredentialsCard: React.FC<{
  demo: boolean;
  settings: IgSettings | null;
  loaded: boolean;
  onSaved: () => Promise<void>;
}> = ({ demo, settings, loaded, onSaved }) => {
  const hasCreds = !!settings && settings.apiKeyMasked !== '**** unset';
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Identifier + account id come back in clear; keys/passwords are masked.
    setIdentifier(settings?.identifier || '');
    setAccountId(settings?.accountId || '');
    setApiKey('');
    setPassword('');
    setEditing(loaded && !hasCreds);
  }, [settings, loaded, hasCreds]);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await svc.saveSettings(demo, {
        identifier,
        accountId,
        ...(apiKey ? { apiKey } : {}),
        ...(password ? { password } : {}),
      });
      setApiKey('');
      setPassword('');
      setEditing(false);
      await onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'h-8 w-full max-w-[400px] rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <Card className={loaded && !hasCreds ? 'border-amber-500/40' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            {demo ? 'Demo' : 'Live'} Credentials (IG)
          </span>
          {hasCreds && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {!editing && hasCreds && settings && (
          <div className="text-xs space-y-0.5">
            <div><span className="text-muted-foreground">identifier </span><span className="font-mono">{settings.identifier || '—'}</span></div>
            <div><span className="text-muted-foreground">account </span><span className="font-mono">{settings.accountId || '—'}</span></div>
            <div><span className="text-muted-foreground">api key </span><span className="font-mono">{settings.apiKeyMasked}</span><span className="text-muted-foreground"> · password </span><span className="font-mono">{settings.passwordMasked}</span></div>
          </div>
        )}

        {editing && (
          <div className="space-y-3">
            <Field label="API Key" required={!hasCreds}>
              <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={hasCreds ? '**** keep existing' : 'Your IG API key for this environment'} className={inputCls} />
              {hasCreds && <span className="text-[10px] text-muted-foreground">Leave blank to keep the existing key.</span>}
            </Field>
            <Field label="Username (identifier)" required={!hasCreds}>
              <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder={demo ? 'demo-yourname' : 'yourname'} className={inputCls} />
              {demo && <span className="text-[10px] text-muted-foreground">Demo logins need the <span className="font-mono">demo-</span> prefix.</span>}
            </Field>
            <Field label="Password" required={!hasCreds}>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={hasCreds ? '**** keep existing' : 'Your IG password'} className={inputCls} />
              {hasCreds && <span className="text-[10px] text-muted-foreground">Leave blank to keep the existing password.</span>}
            </Field>
            <Field label="Account ID" required={false}>
              <input type="text" value={accountId} onChange={(e) => setAccountId(e.target.value)}
                placeholder="e.g. Z4D168" className={inputCls} />
            </Field>
            {error && (
              <div className="text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {error}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={() => void handleSave()}
                disabled={saving || !identifier || (!hasCreds && (!apiKey || !password))}>
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Save
              </Button>
              {hasCreds && (
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setApiKey(''); setPassword(''); }} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Field: React.FC<{ label: string; required: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] text-muted-foreground uppercase tracking-wider">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default IgTradingSettingsPage;
